import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { 
  ChevronLeft, ChevronRight, Calendar, Sun, Moon, Star, Bell, 
  BellRing, X, Clock, Sparkles, Eclipse, PartyPopper
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';
import { ReminderDialog } from '../components/astrology/ReminderDialog';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

// Moon phase icons based on paksha
const getMoonIcon = (tithi) => {
  if (!tithi) return <Moon className="w-4 h-4" />;
  
  if (tithi.tithi_number === 15) return <Sun className="w-4 h-4 text-yellow-600" />; // Purnima - Full Moon
  if (tithi.tithi_number === 30 || tithi.tithi_number === 0) return <Moon className="w-4 h-4 text-[#6B6560]" />; // Amavasya
  if (tithi.paksha === 'shukla') return <Moon className="w-4 h-4 text-yellow-700" fill="currentColor" style={{ clipPath: `inset(0 ${100 - (tithi.moon_phase * 200)}% 0 0)` }} />;
  return <Moon className="w-4 h-4 text-[#6B6560]" />;
};

const HinduCalendarPage = () => {
  const { language, t } = useLanguage();
  const { isDark } = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDetails, setDayDetails] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [userReminders, setUserReminders] = useState([]);
  const [activeTab, setActiveTab] = useState('calendar'); // 'calendar' or 'events'

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => {
    fetchCalendarData();
    fetchUpcomingEvents();
    fetchUserReminders();
  }, [year, month]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/calendar/month/${year}/${month}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error(language === 'hi' ? 'कैलेंडर लोड करने में त्रुटि' : 'Error loading calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/upcoming-events?days=60`);
      setUpcomingEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchUserReminders = async () => {
    try {
      const response = await axios.get(`${API}/calendar/reminders`);
      setUserReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const fetchDayDetails = async (dateStr) => {
    try {
      const response = await axios.get(`${API}/calendar/day/${dateStr}`);
      setDayDetails(response.data);
    } catch (error) {
      console.error('Error fetching day details:', error);
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    fetchDayDetails(day.date);
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
    setSelectedDay(null);
    setDayDetails(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
    setSelectedDay(null);
    setDayDetails(null);
  };

  const deleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/calendar/reminder/${reminderId}`);
      toast.success(language === 'hi' ? 'रिमाइंडर हटाया गया' : 'Reminder deleted');
      fetchUserReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };

  const getDayClass = (day) => {
    const isToday = day.date === new Date().toISOString().split('T')[0];
    const isSelected = selectedDay?.date === day.date;
    const hasEvent = day.festivals?.length > 0 || day.eclipses?.length > 0;
    const hasSpecial = day.tithi?.special_days?.length > 0;
    
    let classes = 'relative p-1 sm:p-2 min-h-[60px] sm:min-h-[70px] rounded-lg cursor-pointer transition-all ';
    
    if (isSelected) {
      classes += isDark ? 'bg-[#C84B32]/30 border-2 border-[#C84B32]' : 'bg-amber-100 border-2 border-amber-500';
    } else if (isToday) {
      classes += 'bg-[#FCE9D9] border-2 border-[#C84B32]';
    } else if (hasEvent) {
      classes += isDark ? 'bg-orange-500/10 hover:bg-orange-500/20' : 'bg-orange-50 hover:bg-orange-100';
    } else if (hasSpecial) {
      classes += isDark ? 'bg-blue-500/10 hover:bg-blue-500/20' : 'bg-blue-50 hover:bg-blue-100';
    } else {
      classes += isDark ? 'hover:bg-[#FCE9D9]/60' : 'hover:bg-[#FFF9EF]';
    }
    
    return classes;
  };

  const weekDays = language === 'hi' 
    ? ['रवि', 'सोम', 'मंगल', 'बुध', 'गुरु', 'शुक्र', 'शनि']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="min-h-screen cosmic-bg pb-28">
      <ShubhHeader title={language === 'hi' ? 'हिंदू पंचांग' : 'Hindu Panchang'} showBack />
      <div className="px-4 pt-3 pb-1 max-w-4xl mx-auto">
        <div className="flex items-center justify-end gap-2 text-[#6B6560]" data-testid="panchang-reminder-count">
          <Bell className="w-4 h-4" />
          <span className="text-sm">{userReminders.length}</span>
        </div>
        {/* Tabs */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'calendar'
                ? isDark ? 'bg-[#C84B32] text-white' : 'bg-[#C84B32] text-white'
                : isDark ? 'bg-[#FCE9D9]/40 text-[#6B6560]' : 'bg-[#FCE9D9]/60 text-[#6B6560]'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            {language === 'hi' ? 'कैलेंडर' : 'Calendar'}
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'events'
                ? isDark ? 'bg-[#C84B32] text-white' : 'bg-[#C84B32] text-white'
                : isDark ? 'bg-[#FCE9D9]/40 text-[#6B6560]' : 'bg-[#FCE9D9]/60 text-[#6B6560]'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            {language === 'hi' ? 'आगामी' : 'Upcoming'}
          </button>
        </div>
      </div>

      {activeTab === 'calendar' ? (
        <div className="p-4 max-w-4xl mx-auto">
          {/* Month Navigator */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={handlePrevMonth} className={`p-2 rounded-full ${isDark ? 'hover:bg-[#FCE9D9]' : 'hover:bg-[#FCE9D9]/60'}`}>
              <ChevronLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#282622]'}`} />
            </button>
            <div className="text-center">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                {calendarData?.month_name} {year}
              </h2>
              {selectedDay && (
                <p className={`text-xs ${isDark ? 'text-[#C84B32]' : 'text-amber-600'}`}>
                  {selectedDay.tithi?.hindu_month}
                </p>
              )}
            </div>
            <button onClick={handleNextMonth} className={`p-2 rounded-full ${isDark ? 'hover:bg-[#FCE9D9]' : 'hover:bg-[#FCE9D9]/60'}`}>
              <ChevronRight className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#282622]'}`} />
            </button>
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day, i) => (
              <div key={day} className={`text-center text-xs font-medium py-2 ${
                i === 0 ? 'text-red-600' : isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'
              }`}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#C84B32] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: new Date(year, month - 1, 1).getDay() }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[60px]" />
              ))}
              
              {/* Calendar Days */}
              {calendarData?.days?.map((day) => (
                <motion.div
                  key={day.date}
                  onClick={() => handleDayClick(day)}
                  className={getDayClass(day)}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        day.weekday === 'Sunday' ? 'text-red-600' : isDark ? 'text-white' : 'text-[#282622]'
                      }`}>
                        {day.day}
                      </span>
                      {getMoonIcon(day.tithi)}
                    </div>
                    
                    {/* Event indicators */}
                    <div className="flex flex-wrap gap-0.5 mt-1">
                      {day.festivals?.length > 0 && (
                        <div className="w-2 h-2 rounded-full bg-orange-400" title={day.festivals[0].name} />
                      )}
                      {day.eclipses?.length > 0 && (
                        <div className="w-2 h-2 rounded-full bg-red-500" title={day.eclipses[0].name} />
                      )}
                      {day.tithi?.special_days?.map((s, i) => (
                        <div 
                          key={i} 
                          className={`w-2 h-2 rounded-full ${
                            s.type === 'purnima' ? 'bg-yellow-400' :
                            s.type === 'amavasya' ? 'bg-[#EDE3D6]' :
                            s.type === 'ekadashi' ? 'bg-[#287A60]' : 'bg-blue-400'
                          }`}
                          title={s.name}
                        />
                      ))}
                    </div>
                    
                    {/* Festival name (truncated) */}
                    {day.festivals?.[0] && (
                      <p className="text-[8px] mt-auto truncate text-orange-600">
                        {language === 'hi' ? day.festivals[0].name_hi : day.festivals[0].name}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Legend */}
          <div className={`mt-4 p-3 rounded-xl ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-[#FFF9EF]'}`}>
            <p className={`text-xs font-medium mb-2 ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
              {language === 'hi' ? 'संकेत' : 'Legend'}
            </p>
            <div className="flex flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-orange-400" />
                <span className={isDark ? 'text-[#4A4640]' : 'text-[#6B6560]'}>{language === 'hi' ? 'त्योहार' : 'Festival'}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className={isDark ? 'text-[#4A4640]' : 'text-[#6B6560]'}>{language === 'hi' ? 'ग्रहण' : 'Eclipse'}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                <span className={isDark ? 'text-[#4A4640]' : 'text-[#6B6560]'}>{language === 'hi' ? 'पूर्णिमा' : 'Purnima'}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-[#287A60]" />
                <span className={isDark ? 'text-[#4A4640]' : 'text-[#6B6560]'}>{language === 'hi' ? 'एकादशी' : 'Ekadashi'}</span>
              </div>
            </div>
          </div>

          {/* Selected Day Details */}
          <AnimatePresence>
            {dayDetails && selectedDay && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className={`mt-4 p-4 rounded-2xl ${isDark ? 'bg-gradient-to-br from-white to-[#FCE9D9] border border-[#282622]/10' : 'bg-white border border-[#282622]/10 shadow-lg'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                      {selectedDay.day} {calendarData?.month_name}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                      {language === 'hi' ? dayDetails.weekday_hi : dayDetails.weekday}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowReminderModal(true)}
                    className="bg-[#C84B32] hover:bg-[#A63D28] text-white"
                  >
                    <BellRing className="w-4 h-4 mr-1" />
                    {language === 'hi' ? 'रिमाइंडर' : 'Remind'}
                  </Button>
                </div>

                {/* Tithi Info */}
                <div className={`p-3 rounded-xl mb-3 ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-amber-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Moon className={`w-5 h-5 ${isDark ? 'text-[#C84B32]' : 'text-amber-600'}`} />
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                      {dayDetails.tithi?.tithi_name}
                    </span>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                    {dayDetails.tithi?.paksha_name} • {dayDetails.tithi?.hindu_month}
                  </p>
                </div>

                {/* Special Days */}
                {dayDetails.tithi?.special_days?.map((special, i) => (
                  <div key={i} className={`p-3 rounded-xl mb-3 ${
                    special.type === 'purnima' ? (isDark ? 'bg-yellow-500/10' : 'bg-yellow-50') :
                    special.type === 'amavasya' ? (isDark ? 'bg-gray-500/10' : 'bg-[#FCE9D9]/60') :
                    special.type === 'ekadashi' ? (isDark ? 'bg-[#287A60]/10' : 'bg-[#287A60]/10') :
                    (isDark ? 'bg-blue-500/10' : 'bg-blue-50')
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Star className={`w-4 h-4 ${
                        special.type === 'purnima' ? 'text-yellow-600' :
                        special.type === 'ekadashi' ? 'text-[#287A60]' : 'text-blue-600'
                      }`} />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                        {special.name}
                      </span>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                      {special.significance}
                    </p>
                  </div>
                ))}

                {/* Festivals */}
                {dayDetails.festivals?.map((festival, i) => (
                  <div key={i} className={`p-3 rounded-xl mb-3 ${isDark ? 'bg-orange-500/10' : 'bg-orange-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <PartyPopper className="w-4 h-4 text-orange-600" />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                        {language === 'hi' ? festival.name_hi : festival.name}
                      </span>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                      {festival.description}
                    </p>
                  </div>
                ))}

                {/* Eclipses */}
                {dayDetails.eclipses?.map((eclipse, i) => (
                  <div key={i} className={`p-3 rounded-xl mb-3 ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Eclipse className="w-4 h-4 text-red-600" />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                        {language === 'hi' ? eclipse.name_hi : eclipse.name}
                      </span>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                      {eclipse.visibility} • {eclipse.time}
                    </p>
                  </div>
                ))}

                {/* Auspicious Activities */}
                {dayDetails.auspicious_activities && (
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                    <p className={`text-sm font-medium mb-2 ${isDark ? 'text-green-600' : 'text-green-700'}`}>
                      {language === 'hi' ? 'शुभ कार्य' : 'Auspicious For'}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {dayDetails.auspicious_activities.good_for?.slice(0, 4).map((activity, i) => (
                        <span key={i} className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-green-500/20 text-green-700' : 'bg-green-100 text-green-700'}`}>
                          {activity}
                        </span>
                      ))}
                    </div>
                    {dayDetails.auspicious_activities.avoid?.length > 0 && (
                      <>
                        <p className={`text-sm font-medium mt-3 mb-2 ${isDark ? 'text-red-600' : 'text-red-700'}`}>
                          {language === 'hi' ? 'टालें' : 'Avoid'}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {dayDetails.auspicious_activities.avoid?.slice(0, 3).map((activity, i) => (
                            <span key={i} className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-red-500/20 text-red-700' : 'bg-red-100 text-red-700'}`}>
                              {activity}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        /* Upcoming Events Tab */
        <div className="p-4 space-y-4 max-w-4xl mx-auto">
          {/* Upcoming Festivals */}
          <div className={`rounded-2xl p-4 ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
              <PartyPopper className="w-5 h-5 text-orange-600" />
              {language === 'hi' ? 'आगामी त्योहार' : 'Upcoming Festivals'}
            </h3>
            <div className="space-y-2">
              {upcomingEvents?.festivals?.slice(0, 5).map((festival, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-orange-50'}`}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                      {language === 'hi' ? festival.name_hi : festival.name}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                      {new Date(festival.date).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long' })}
                    </p>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-orange-500/20 text-orange-700' : 'bg-orange-100 text-orange-700'}`}>
                    {Math.ceil((new Date(festival.date) - new Date()) / (1000 * 60 * 60 * 24))} {language === 'hi' ? 'दिन' : 'days'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Eclipses */}
          <div className={`rounded-2xl p-4 ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
              <Eclipse className="w-5 h-5 text-red-600" />
              {language === 'hi' ? 'आगामी ग्रहण' : 'Upcoming Eclipses'}
            </h3>
            <div className="space-y-2">
              {upcomingEvents?.eclipses?.slice(0, 3).map((eclipse, i) => (
                <div key={i} className={`p-3 rounded-xl ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-red-50'}`}>
                  <div className="flex items-center justify-between">
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                      {language === 'hi' ? eclipse.name_hi : eclipse.name}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      eclipse.type === 'surya_grahan' 
                        ? (isDark ? 'bg-yellow-500/20 text-yellow-700' : 'bg-yellow-100 text-yellow-700')
                        : (isDark ? 'bg-gray-500/20 text-[#4A4640]' : 'bg-[#FCE9D9]/60 text-[#4A4640]')
                    }`}>
                      {eclipse.type === 'surya_grahan' ? (language === 'hi' ? 'सूर्य' : 'Solar') : (language === 'hi' ? 'चंद्र' : 'Lunar')}
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                    {new Date(eclipse.date).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                    {eclipse.visibility}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Special Tithis */}
          <div className={`rounded-2xl p-4 ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
              <Moon className="w-5 h-5 text-[#287A60]" />
              {language === 'hi' ? 'विशेष तिथियां' : 'Special Tithis'}
            </h3>
            <div className="space-y-2">
              {upcomingEvents?.special_tithis?.map((item, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-[#287A60]/10'}`}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                      {item.special_days[0]?.name}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                      {new Date(item.date).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long' })}
                    </p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    item.special_days[0]?.type === 'purnima' ? 'bg-yellow-400' :
                    item.special_days[0]?.type === 'amavasya' ? 'bg-[#EDE3D6]' :
                    item.special_days[0]?.type === 'ekadashi' ? 'bg-[#287A60]' : 'bg-blue-400'
                  }`} />
                </div>
              ))}
            </div>
          </div>

          {/* User Reminders */}
          {userReminders.length > 0 && (
            <div className={`rounded-2xl p-4 ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                <BellRing className="w-5 h-5 text-[#C84B32]" />
                {language === 'hi' ? 'मेरे रिमाइंडर' : 'My Reminders'}
              </h3>
              <div className="space-y-2">
                {userReminders.map((reminder, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-amber-50'}`}>
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                        {reminder.title}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                        {reminder.date} at {reminder.time}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteReminder(reminder.reminder_id)}
                      className={`p-2 rounded-full ${isDark ? 'hover:bg-[#FCE9D9]' : 'hover:bg-[#FCE9D9]/60'}`}
                    >
                      <X className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && selectedDay && <ReminderDialog
        event={{ title: selectedDay.festivals?.[0]?.name || selectedDay.eclipses?.[0]?.name || selectedDay.tithi?.special_days?.[0]?.name || selectedDay.tithi?.tithi_name || 'Calendar event',
          starts_at: `${selectedDay.date}T09:00:00`, event_id: `calendar-${selectedDay.date}`, url: '/hindu-calendar' }}
        onSaved={fetchUserReminders} onClose={() => setShowReminderModal(false)} />}
      <ShubhBottomNav />
    </div>
  );
};

export default HinduCalendarPage;
